import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtUSD, fmtPct, fmtNum, gainClass } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LineChart, Line, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import { toast } from "sonner";
import { Play, TrendingUp, Zap } from "lucide-react";

const DEFAULT_DAYS = 90;

export default function Backtest() {
  const [assets, setAssets] = useState([]);
  const [strategies, setStrategies] = useState({});
  const [strategy, setStrategy] = useState("sma_cross");
  const [symbol, setSymbol] = useState("BTC");
  const [assetType, setAssetType] = useState("crypto");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [params, setParams] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/market/assets").then(({ data }) => setAssets(data)).catch(() => {});
    api.get("/backtest/strategies").then(({ data }) => setStrategies(data)).catch(() => {});
    api.get("/backtest/history").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);

  useEffect(() => {
    // reset params to strategy defaults
    const s = strategies[strategy];
    if (!s) return;
    const defaults = {};
    for (const [k, v] of Object.entries(s.params)) defaults[k] = v.default;
    setParams(defaults);
  }, [strategy, strategies]);

  const stratMeta = strategies[strategy];

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/backtest/run", {
        symbol,
        asset_type: assetType,
        strategy,
        params,
        days: Number(days),
      });
      setResult(data);
      toast.success("Backtest complete");
      api.get("/backtest/history").then(({ data }) => setHistory(data)).catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backtest failed");
    } finally {
      setBusy(false);
    }
  };

  const chartData = useMemo(
    () => (result?.equity_curve || []).map((p) => ({ t: p.t, equity: p.equity, price: p.price })),
    [result]
  );

  return (
    <div className="space-y-8" data-testid="backtest-root">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Backtest</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-2">
          Test strategies against history
        </h1>
        <p className="text-gray-500 mt-2 max-w-2xl">
          Pick a preset, tune parameters, and run it against historical prices. Compare vs buy-and-hold with drawdown, Sharpe, and equity curve.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Config */}
        <Card className="lg:col-span-4 border border-gray-200 rounded-2xl p-6 h-fit sticky top-24" data-testid="backtest-config">
          <p className="font-display font-semibold text-lg">Configuration</p>

          <div className="mt-4 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Asset</Label>
              <Select
                value={`${assetType}:${symbol}`}
                onValueChange={(v) => {
                  const [t, s] = v.split(":");
                  setAssetType(t);
                  setSymbol(s);
                }}
              >
                <SelectTrigger data-testid="backtest-asset" className="h-11 rounded-xl border-gray-200 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {assets.map((a) => (
                    <SelectItem key={`${a.asset_type}:${a.symbol}`} value={`${a.asset_type}:${a.symbol}`}>
                      <span className="font-medium mr-2">{a.symbol}</span>
                      <span className="text-xs text-gray-500">{a.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Strategy</Label>
              <Select value={strategy} onValueChange={setStrategy}>
                <SelectTrigger data-testid="backtest-strategy" className="h-11 rounded-xl border-gray-200 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(strategies).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {stratMeta && <p className="text-xs text-gray-500 mt-2">{stratMeta.description}</p>}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Period (days)</Label>
              <Input
                data-testid="backtest-days"
                type="number"
                min={30}
                max={365}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
              />
            </div>

            {stratMeta &&
              Object.entries(stratMeta.params).map(([key, meta]) => (
                <div key={key}>
                  <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">
                    {key} <span className="text-gray-400 normal-case font-normal">({meta.min}–{meta.max})</span>
                  </Label>
                  <Input
                    data-testid={`backtest-param-${key}`}
                    type="number"
                    step="any"
                    min={meta.min}
                    max={meta.max}
                    value={params[key] ?? meta.default}
                    onChange={(e) => setParams({ ...params, [key]: e.target.value })}
                    className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
                  />
                </div>
              ))}

            <Button
              onClick={run}
              disabled={busy}
              data-testid="backtest-run"
              className="w-full h-11 rounded-full bg-brand text-white hover:bg-brand-deep font-medium"
            >
              <Play size={14} className="mr-2" /> {busy ? "Running…" : "Run backtest"}
            </Button>
          </div>
        </Card>

        {/* Result */}
        <div className="lg:col-span-8 space-y-6">
          {!result ? (
            <Card className="border border-gray-200 rounded-2xl p-10 text-center text-gray-500">
              <Zap className="mx-auto mb-3 text-gray-300" size={32} />
              <p>Run a backtest to see the equity curve and stats.</p>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="backtest-stats">
                <StatCard
                  label="Return"
                  value={fmtPct(result.stats.total_return_pct)}
                  sub={fmtUSD(result.stats.final_equity)}
                  cls={gainClass(result.stats.total_return_pct)}
                  testId="stat-return"
                />
                <StatCard
                  label="Buy & Hold"
                  value={fmtPct(result.stats.buy_hold_return_pct)}
                  sub="Benchmark"
                  cls={gainClass(result.stats.buy_hold_return_pct)}
                  testId="stat-bh"
                />
                <StatCard
                  label="Max drawdown"
                  value={fmtPct(result.stats.max_drawdown_pct)}
                  sub="Peak to trough"
                  cls="text-loss"
                  testId="stat-dd"
                />
                <StatCard
                  label="Sharpe (ann.)"
                  value={result.stats.sharpe.toFixed(2)}
                  sub={`${result.stats.num_trades} trades · ${result.stats.win_rate.toFixed(0)}% win`}
                  testId="stat-sharpe"
                />
              </div>

              <Card className="border border-gray-200 rounded-2xl p-6" data-testid="backtest-chart">
                <div className="flex items-center justify-between mb-4">
                  <p className="font-display font-semibold text-lg flex items-center gap-2">
                    <TrendingUp size={18} /> Equity curve
                  </p>
                  <p className="text-xs text-gray-500 font-mono">Start: {fmtUSD(result.stats.starting_equity)}</p>
                </div>
                <div style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                      <YAxis hide domain={["dataMin", "dataMax"]} />
                      <Tooltip
                        cursor={{ stroke: "#e5e7eb" }}
                        contentStyle={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          fontFamily: "Manrope",
                          fontSize: 12,
                          padding: "8px 12px",
                        }}
                        labelFormatter={(t) => new Date(t).toLocaleDateString()}
                        formatter={(v, name) => [fmtUSD(v), name === "equity" ? "Strategy" : "Price"]}
                      />
                      <Line
                        type="monotone"
                        dataKey="equity"
                        stroke={result.stats.total_return_pct >= 0 ? "#16a34a" : "#dc2626"}
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {result.trades?.length > 0 && (
                <Card className="border border-gray-200 rounded-2xl p-6" data-testid="backtest-trades">
                  <p className="font-display font-semibold text-lg mb-4">Simulated trades</p>
                  <div className="divide-y divide-gray-100 max-h-80 overflow-auto">
                    {result.trades.map((t, i) => (
                      <div key={i} className="flex items-center justify-between py-2 text-sm">
                        <span className={t.side === "buy" ? "text-gain font-medium" : "text-loss font-medium"}>
                          {t.side.toUpperCase()}
                        </span>
                        <span className="font-mono">{fmtNum(t.qty, 6)}</span>
                        <span className="font-mono">{fmtUSD(t.price, { dp: t.price > 10 ? 2 : 4 })}</span>
                        <span className="text-xs text-gray-500">{new Date(t.t).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}

          {history.length > 0 && (
            <Card className="border border-gray-200 rounded-2xl p-6" data-testid="backtest-history">
              <p className="font-display font-semibold text-lg mb-4">Recent backtests</p>
              <div className="divide-y divide-gray-100">
                {history.map((h) => (
                  <div key={h._id} className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm font-medium">
                        {h.symbol} · {strategies[h.strategy]?.name || h.strategy}
                      </p>
                      <p className="text-xs text-gray-500">
                        {h.days}d · {new Date(h.created_at).toLocaleString()}
                      </p>
                    </div>
                    <p className={`text-sm font-mono font-medium ${gainClass(h.stats.total_return_pct)}`}>
                      {fmtPct(h.stats.total_return_pct)}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, cls = "", testId }) {
  return (
    <Card className="border border-gray-200 rounded-2xl p-4" data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">{label}</p>
      <p className={`font-display text-2xl font-medium tabular-nums mt-1 ${cls}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
    </Card>
  );
}
